import React, { useCallback, useMemo } from 'react';
import {
  Button,
  Dialog,
  DownloadButton,
  EmptyContent,
  Flex,
} from '@/components/ui';
import { FilePreviewModalProps } from './types';
import { canPreviewFile, renderFilePreview } from './PreviewRegistry';
import { getFileTypeName } from './utils/fileTypeDetection';
import { usePermissionAccess } from '@/hooks/usePermissionAccess';
import { PERMISSIONS } from '@/types/permissions';
import {
  IconAlertCircle,
  IconDownload,
  IconFileUnknown,
  IconLockAccess,
  IconX,
} from '@tabler/icons-react';

import './_FilePreviewModal.scss';
import { formatFileSize } from '@/utils/fileUtils.ts';

export const FilePreviewModal: React.FC<FilePreviewModalProps> = ({
  apiEndpoint,
  fileUrl,
  filename,
  mimeType,
  fileSize,
  open,
  onOpenChange,
  title,
  showDownloadButton = true,
  hasPreviewPermission,
}) => {
  // Use provided permission or default to STUDY_FILES_ACCESS for study files
  const defaultManageFilesPermission = usePermissionAccess([
    PERMISSIONS.STUDY_FILES_ACCESS,
  ]);
  const hasManageFilesPermission =
    hasPreviewPermission !== undefined
      ? hasPreviewPermission
      : defaultManageFilesPermission;

  // Memoize computed values
  const canPreview = useMemo(
    () => canPreviewFile(filename, mimeType, fileSize),
    [filename, mimeType, fileSize]
  );

  const fileTypeName = useMemo(
    () => getFileTypeName(filename, mimeType),
    [filename, mimeType]
  );

  const modalTitle = useMemo(
    () => title || `Preview: ${filename}`,
    [title, filename]
  );

  // Stable close handler
  const handleClose = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  // Memoize content rendering
  const renderContent = useCallback(() => {
    // No permission
    if (!hasManageFilesPermission) {
      return (
        <EmptyContent
          size="medium"
          // withIconBox
          state="error"
          icon={<IconLockAccess />}
          title="Access Denied"
          description="You don't have permission to preview files. Please contact your administrator."
        >
          {showDownloadButton && apiEndpoint && (
            <DownloadButton
              apiEndpoint={apiEndpoint}
              filename={filename}
              variant="outlined"
              startIcon={<IconDownload />}
            >
              Download File
            </DownloadButton>
          )}
        </EmptyContent>
      );
    }

    // Preview not supported
    if (!canPreview) {
      return (
        <EmptyContent
          size="medium"
          icon={<IconFileUnknown />}
          title="Preview Not Available"
          description={`Preview is not currently supported for ${fileTypeName} files. You can download the file to view it locally.`}
        >
          {showDownloadButton && apiEndpoint && (
            <DownloadButton
              apiEndpoint={apiEndpoint}
              filename={filename}
              variant="outlined"
              startIcon={<IconDownload />}
            >
              Download File
            </DownloadButton>
          )}
        </EmptyContent>
      );
    }

    // Render preview with stable key
    if (!apiEndpoint && !fileUrl) {
      return (
        <EmptyContent
          size="medium"
          icon={<IconAlertCircle />}
          title="Preview Not Available"
          description="API endpoint or file URL is required for file preview."
        />
      );
    }

    const previewProps = {
      apiEndpoint,
      fileUrl,
      filename,
      mimeType,
      fileSize,
    };

    return (
      <div key={`preview-${filename}-${fileSize}`}>
        {renderFilePreview(previewProps)}
      </div>
    );
  }, [
    hasManageFilesPermission,
    canPreview,
    fileTypeName,
    showDownloadButton,
    apiEndpoint,
    fileUrl,
    filename,
    mimeType,
    fileSize,
  ]);

  // Only render content when modal is open
  const content = useMemo(() => {
    if (!open) return null;
    return renderContent();
  }, [open, renderContent]);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content
        className="file-preview-modal"
        size="medium"
        style={{
          maxWidth: '90vw',
          width: canPreview ? 'auto' : '1200px',
          minWidth: canPreview ? '600px' : 'auto',
          minHeight: '600px',
        }}
      >
        <Dialog.Header
          title={modalTitle}
          subtitle={`${fileTypeName}${fileSize ? ` • ${formatFileSize(fileSize)}` : ''}`}
          endSlot={
            <Flex align="center" gap="1">
              {showDownloadButton &&
                hasManageFilesPermission &&
                apiEndpoint && (
                  <DownloadButton
                    apiEndpoint={apiEndpoint}
                    filename={filename}
                    variant="text"
                    isIconOnly
                    title="Download file"
                    startIcon={<IconDownload />}
                  />
                )}

              <Button
                variant="text"
                isIconOnly
                onClick={handleClose}
                title="Close"
                startIcon={<IconX />}
              />
            </Flex>
          }
        />

        <Dialog.Body>
          {/*<div*/}
          {/*  className="file-preview-modal__body"*/}
          {/*  key={`modal-body-${filename}-${apiEndpoint}`}*/}
          {/*>*/}
          {content}
          {/*</div>*/}
        </Dialog.Body>
      </Dialog.Content>
    </Dialog.Root>
  );
};
